(function () {
  "use strict";

  // Tiered free-gift picker (<cart-gwp>) for the quick-cart drawer and the cart
  // page. Setup, architecture and decisions: docs/gwp.md.

  const LINE_PROPERTY = "_gwp_tier";
  const READY_CLASS = "gwp-ready";
  const CONFIG = normalizeConfig(window.flu && window.flu.gwpConfig);

  if (!CONFIG) return;

  function normalizeConfig(raw) {
    if (!raw || typeof raw !== "object") return null;
    const out = {};
    ["tier1", "tier2"].forEach((key) => {
      const t = raw[key];
      if (!t) return;
      const threshold = parseFloat(t.threshold);
      const handles = Array.isArray(t.giftHandles)
        ? t.giftHandles.filter((h) => typeof h === "string" && h.length > 0)
        : [];
      if (!isFinite(threshold) || threshold <= 0 || handles.length === 0)
        return;
      out[key] = {
        tier: key,
        thresholdCents: Math.round(threshold * 100),
        handles,
      };
    });
    return out.tier1 || out.tier2 ? out : null;
  }

  const ALL_HANDLES = (() => {
    const seen = new Set();
    Object.values(CONFIG).forEach((t) => t.handles.forEach((h) => seen.add(h)));
    return [...seen];
  })();

  function activeTierForSubtotal(cents) {
    if (CONFIG.tier2 && cents >= CONFIG.tier2.thresholdCents)
      return CONFIG.tier2;
    if (CONFIG.tier1 && cents >= CONFIG.tier1.thresholdCents)
      return CONFIG.tier1;
    return null;
  }

  function unlockThresholdForHandle(handle) {
    let min = Infinity;
    Object.values(CONFIG).forEach((t) => {
      if (t.handles.indexOf(handle) !== -1 && t.thresholdCents < min)
        min = t.thresholdCents;
    });
    return min === Infinity ? null : min;
  }

  function qualifyingTierForHandle(handle, subtotalCents) {
    let out = null;
    Object.values(CONFIG).forEach((t) => {
      if (t.handles.indexOf(handle) === -1 || subtotalCents < t.thresholdCents)
        return;
      if (!out || t.thresholdCents < out.thresholdCents) out = t;
    });
    return out;
  }

  function fetchJson(url, init) {
    const opts = Object.assign(
      { headers: { Accept: "application/json" } },
      init || {},
    );
    return fetch(url, opts).then((r) => {
      if (r.ok) return r.json();
      return r.json().then(
        (e) => {
          throw new Error(e.description || e.message || "Request failed");
        },
        () => {
          throw new Error("Request failed");
        },
      );
    });
  }

  const THEME_ROUTES = (window.theme && window.theme.routes) || {};
  const CART_ROUTES = THEME_ROUTES.cart || {};
  const CART_URL = (CART_ROUTES.base || "/cart") + ".js";
  const CART_ADD_URL = (CART_ROUTES.add || "/cart/add") + ".js";
  const CART_CHANGE_URL = (CART_ROUTES.change || "/cart/change") + ".js";
  const PRODUCTS_URL = THEME_ROUTES.products || "/products";

  const fetchCart = () => fetchJson(CART_URL);

  const setLineQuantity = (key, quantity) =>
    fetchJson(CART_CHANGE_URL, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ id: key, quantity }),
    });

  const removeLine = (key) => setLineQuantity(key, 0);

  const addGiftVariant = (variantId, tierKey) =>
    fetchJson(CART_ADD_URL, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        items: [
          {
            id: variantId,
            quantity: 1,
            properties: { [LINE_PROPERTY]: tierKey },
          },
        ],
      }),
    });

  const productPromises = new Map();
  function fetchProduct(handle) {
    if (productPromises.has(handle)) return productPromises.get(handle);
    const p = fetchJson(PRODUCTS_URL + "/" + handle + ".js").catch(() => {
      productPromises.delete(handle);
      return null;
    });
    productPromises.set(handle, p);
    return p;
  }

  function pickGiftVariant(product) {
    if (!product || !product.variants || product.variants.length === 0)
      return null;
    for (const v of product.variants)
      if (v.available !== false && v.price === 0) return v;
    return null;
  }

  // Same two emissions as theme.js (addItemById / changeItem); skipping either
  // leaves the drawer or the cart page stale.
  function dispatchCartUpdated(cart) {
    const flu = window.flu && window.flu.events;
    if (flu && typeof flu.emit === "function") {
      flu.emit("quick-cart:updated");
      flu.emit("cart:updated", { cart });
    }
    document.dispatchEvent(
      new CustomEvent("cart:updated", { detail: { cart } }),
    );
  }

  function qualifyingSubtotalCents(cart) {
    return cart.items.reduce(
      (sum, item) =>
        item.properties && item.properties[LINE_PROPERTY]
          ? sum
          : sum + item.final_line_price,
      0,
    );
  }

  function giftLines(cart) {
    return cart.items.filter(
      (item) => item.properties && item.properties[LINE_PROPERTY],
    );
  }

  function tierConfigFor(line) {
    const raw = line.properties && line.properties[LINE_PROPERTY];
    return (raw && CONFIG[raw]) || null;
  }

  const INITIAL = Object.freeze({
    cart: null,
    products: Object.freeze({}),
    productsReady: false,
    panelOpen: false,
    pending: null, // 'picking:<handle>' | 'declining' | null
    status: "",
    lostGift: false,
  });

  let state = INITIAL;
  const subscribers = new Set();

  function setState(patch) {
    const next = Object.freeze(Object.assign({}, state, patch));
    let changed = false;
    for (const k in next)
      if (next[k] !== state[k]) {
        changed = true;
        break;
      }
    if (!changed) return;
    state = next;
    subscribers.forEach((fn) => {
      try {
        fn(state);
      } catch (_) {
        /* keep others alive */
      }
    });
  }

  function subscribe(fn) {
    subscribers.add(fn);
    return () => subscribers.delete(fn);
  }

  let booted = false;
  let enforcing = false;
  let enforceAgain = false;

  function forgetProduct(handle) {
    const products = Object.assign({}, state.products);
    delete products[handle];
    setState({ products: Object.freeze(products) });
  }

  function loadProduct(handle, force) {
    return fetchProduct(handle).then(async (prod) => {
      if (!prod) {
        if (force) forgetProduct(handle);
        return;
      }
      if (state.products[handle] && !force) return;
      await preloadImage(prod.featured_image, TILE_IMAGE_WIDTH);
      setState({
        products: Object.freeze(
          Object.assign({}, state.products, { [handle]: prod }),
        ),
      });
    });
  }

  function refreshProduct(handle) {
    productPromises.delete(handle);
    loadProduct(handle, true);
  }

  function loadMissingProducts() {
    const missing = ALL_HANDLES.filter((h) => !state.products[h]);
    if (missing.length === 0) return;
    Promise.all(missing.map((handle) => loadProduct(handle))).then(() =>
      setState({ productsReady: true }),
    );
  }

  function boot() {
    if (booted) return;
    booted = true;
    fetchCart()
      .then((bootCart) => {
        // A cart:updated that landed while this request was in flight is newer.
        const cart = state.cart || bootCart;
        setState({ cart });
        document.documentElement.classList.add(READY_CLASS);
        enforce(cart);
      })
      .catch(() => {
        booted = false;
      });
  }

  function togglePanel() {
    const opening = !state.panelOpen;
    setState({ panelOpen: opening, status: "" });
    if (opening) loadMissingProducts();
  }

  async function settle(mutated, error, observed) {
    let cart = mutated ? null : observed || null;
    if (mutated) {
      try {
        cart = await fetchCart();
      } catch (err) {
        error = error || err;
      }
    }
    const patch = { pending: null };
    if (cart) patch.cart = cart;
    if (error) {
      patch.panelOpen = true;
      patch.status = error.message || "Something went wrong. Try again.";
    } else {
      patch.status = "";
      patch.lostGift = false;
    }
    setState(patch);
    if (cart) dispatchCartUpdated(cart);
    else if (enforceAgain) enforce();
    enforceAgain = false;
  }

  async function pickGift(handle) {
    if (state.pending || enforcing) return;
    setState({ pending: "picking:" + handle, panelOpen: false, status: "" });
    let mutated = false;
    let observed = null;
    let error = null;
    try {
      const variant = pickGiftVariant(await fetchProduct(handle));
      if (!variant) throw new Error("This gift is unavailable. Try another.");
      const cart = await fetchCart();
      observed = cart;
      const tier = qualifyingTierForHandle(
        handle,
        qualifyingSubtotalCents(cart),
      );
      if (!tier)
        throw new Error("This gift is no longer available for your cart.");
      const previous = giftLines(cart);
      const matching = previous.find(
        (line) =>
          line.variant_id === variant.id &&
          line.properties[LINE_PROPERTY] === tier.tier,
      );
      // Add before removing, so a failed add keeps the gift the shopper has.
      mutated = true;
      if (!matching) {
        try {
          await addGiftVariant(variant.id, tier.tier);
        } catch (err) {
          refreshProduct(handle);
          throw err;
        }
      }
      for (const line of previous) {
        if (line !== matching) await removeLine(line.key);
      }
    } catch (err) {
      error = err;
    }
    await settle(mutated, error, observed);
  }

  async function declineGifts() {
    if (state.pending || enforcing) return;
    setState({ pending: "declining", panelOpen: false, status: "" });
    let mutated = false;
    let observed = null;
    let error = null;
    try {
      observed = await fetchCart();
      const lines = giftLines(observed);
      mutated = lines.length > 0;
      for (const line of lines) await removeLine(line.key);
    } catch (err) {
      error = err;
    }
    await settle(mutated, error, observed);
  }

  // Skips while a pick/decline or another run is active, and re-runs once
  // afterwards (enforceAgain). See docs/gwp.md.
  async function enforce(providedCart) {
    if (state.pending || enforcing) {
      enforceAgain = true;
      return;
    }
    enforcing = true;
    try {
      const cart = providedCart || (await fetchCart());
      const gifts = giftLines(cart);
      const subtotalCents = qualifyingSubtotalCents(cart);
      const activeTier = activeTierForSubtotal(subtotalCents);
      const lostGift = state.lostGift && !activeTier;
      if (gifts.length === 0) {
        setState({ cart, lostGift });
        return;
      }

      const toRemove = [];
      let kept = null;
      gifts.forEach((line) => {
        const tier = tierConfigFor(line);
        const stillQualifies =
          tier &&
          line.final_line_price === 0 &&
          subtotalCents >= tier.thresholdCents &&
          tier.handles.indexOf(line.handle) !== -1;
        if (!stillQualifies) toRemove.push(line);
        else if (kept) {
          toRemove.push(kept);
          kept = line;
        } else kept = line;
      });

      const fixQuantity = kept !== null && kept.quantity > 1;
      if (toRemove.length === 0 && !fixQuantity) {
        setState({ cart, lostGift: false });
        return;
      }

      for (const line of toRemove) await removeLine(line.key);
      if (fixQuantity) await setLineQuantity(kept.key, 1);
      const next = await fetchCart();
      setState({
        cart: next,
        lostGift: kept === null && activeTier === null,
      });
      dispatchCartUpdated(next);
    } catch (_) {
      // Failed part-way: publish the real cart instead of a stale one. The
      // dispatch re-enters enforce(), so drop that request rather than loop.
      try {
        const cart = await fetchCart();
        setState({ cart });
        dispatchCartUpdated(cart);
        enforceAgain = false;
      } catch (__) {
        // offline: nothing to show
      }
    } finally {
      enforcing = false;
      if (enforceAgain) {
        enforceAgain = false;
        enforce();
      }
    }
  }

  function escapeHtml(s) {
    return String(s)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#39;");
  }

  const THUMB_IMAGE_WIDTH = 32;
  const TILE_IMAGE_WIDTH = 80;

  function sizedImageUrl(url, width) {
    try {
      const sized = new URL(url, window.location.href);
      sized.searchParams.set("width", width);
      return sized.toString();
    } catch (_) {
      return url;
    }
  }

  function imageSources(url, cssWidth) {
    return {
      src: sizedImageUrl(url, cssWidth * 2),
      srcset: [1, 2, 3]
        .map(
          (density) =>
            sizedImageUrl(url, cssWidth * density) + " " + density + "x",
        )
        .join(", "),
    };
  }

  function renderImg(url, cssWidth) {
    if (!url)
      return '<span class="cart-gwp__no-image" aria-hidden="true"></span>';
    const { src, srcset } = imageSources(url, cssWidth);
    return (
      '<img src="' +
      escapeHtml(src) +
      '" srcset="' +
      escapeHtml(srcset) +
      '" alt="" decoding="async" />'
    );
  }

  const IMAGE_PRELOAD_TIMEOUT_MS = 2000;

  function preloadImage(url, cssWidth) {
    if (!url) return Promise.resolve();
    const { src, srcset } = imageSources(url, cssWidth);
    const img = new Image();
    img.srcset = srcset;
    img.src = src;
    const decoded = img.decode().catch(() => {});
    const timeout = new Promise((resolve) =>
      setTimeout(resolve, IMAGE_PRELOAD_TIMEOUT_MS),
    );
    return Promise.race([decoded, timeout]);
  }

  function formatMoney(cents) {
    const dollars = (cents / 100).toFixed(2).replace(/\.00$/, "");
    const fmt = (window.theme && window.theme.moneyFormat) || "${{amount}}";
    return fmt.replace(/\{\{\s*amount\s*\}\}/i, dollars);
  }

  function computeView(cart) {
    if (!cart) return null;
    const subtotalCents = qualifyingSubtotalCents(cart);
    const activeTier = activeTierForSubtotal(subtotalCents);
    const existingGift = giftLines(cart)[0] || null;

    let ui;
    if (existingGift) ui = "chosen";
    else if (activeTier) ui = "unlocked";
    else if (state.lostGift) ui = "dropped";
    else ui = "locked";

    let nextThresholdCents = null;
    Object.values(CONFIG).forEach((cfg) => {
      if (cfg.thresholdCents > subtotalCents) {
        const diff = cfg.thresholdCents - subtotalCents;
        if (nextThresholdCents == null || diff < nextThresholdCents)
          nextThresholdCents = diff;
      }
    });

    // Still loading counts as pickable; "none available" waits for the requests.
    const hasPickableGift =
      !state.productsReady ||
      ALL_HANDLES.some(
        (handle) =>
          pickGiftVariant(state.products[handle]) &&
          unlockThresholdForHandle(handle) <= subtotalCents,
      );

    return {
      ui,
      subtotalCents,
      existingGift,
      nextThresholdCents,
      hasPickableGift,
    };
  }

  const ICON_CHECK =
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.25" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><path d="M8 12.5l2.5 2.5L16 9.5"/></svg>';
  const ICON_CHEVRON =
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M6 9l6 6 6-6"/></svg>';
  const ICON_LOCK =
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"><rect x="5" y="11" width="14" height="10" rx="1.5"/><path d="M8 11V8a4 4 0 1 1 8 0v3"/></svg>';
  const ICON_NOPE =
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"><circle cx="12" cy="12" r="9"/><line x1="6" y1="18" x2="18" y2="6"/></svg>';

  // Built once: the aria-live status node must stay mounted to be announced.
  function ensureSkeleton(el) {
    if (el._body) return;
    el.innerHTML =
      '<div data-gwp-head></div><div class="cart-gwp__card">' +
      '<div data-gwp-body></div>' +
      '<p class="cart-gwp__status" data-gwp-status aria-live="polite"></p>' +
      "</div>";
    el._head = el.querySelector("[data-gwp-head]");
    el._body = el.querySelector("[data-gwp-body]");
    el._status = el.querySelector("[data-gwp-status]");
  }

  function focusedControl(el) {
    const active = document.activeElement;
    const control =
      active && el.contains(active)
        ? active.closest(
            "[data-gwp-toggle], [data-gwp-decline], [data-gwp-handle]",
          )
        : null;
    if (!control) return null;
    if (control.hasAttribute("data-gwp-toggle")) return "[data-gwp-toggle]";
    if (control.hasAttribute("data-gwp-decline")) return "[data-gwp-decline]";
    const handle = CSS.escape(control.getAttribute("data-gwp-handle"));
    return '[data-gwp-handle="' + handle + '"]';
  }

  function restoreFocus(el, selector) {
    const waiting = el._refocus && document.activeElement === document.body;
    if (!selector && !waiting) {
      el._refocus = false;
      return;
    }
    const target = [selector, "[data-gwp-toggle]"]
      .map((s) => s && el.querySelector(s))
      .find((node) => node && !node.disabled);
    if (target) target.focus();
    el._refocus = !target;
  }

  function render(el) {
    ensureSkeleton(el);
    const view = computeView(state.cart);
    if (!view) {
      el.setAttribute("data-state", "loading");
      return;
    }

    const focused = focusedControl(el);
    const isPending = !!state.pending;
    const nothingToPick = view.ui === "unlocked" && !view.hasPickableGift;
    const canToggle =
      ((view.ui === "unlocked" && !nothingToPick) || view.ui === "chosen") &&
      !isPending;
    const showPanel = canToggle && state.panelOpen;
    const showEyebrow =
      (view.ui === "unlocked" && !nothingToPick) ||
      view.ui === "chosen" ||
      isPending;

    el.setAttribute("data-state", view.ui);
    el._head.innerHTML = showEyebrow
      ? '<p class="cart-gwp__eyebrow">You&rsquo;ve unlocked a free gift</p>'
      : "";
    el._body.innerHTML =
      '<div class="cart-gwp__strip">' +
      renderStrip(view) +
      (canToggle ? renderToggle(view) : "") +
      "</div>" +
      (showPanel
        ? '<div class="cart-gwp__panel">' + renderPanel(view) + "</div>"
        : "");
    el._status.textContent = state.status;
    restoreFocus(el, focused);
  }

  function renderStrip(view) {
    if (state.pending && state.pending.indexOf("picking:") === 0) {
      const h = state.pending.slice("picking:".length);
      const product = state.products[h];
      const name = (product && product.title) || h;
      const img = (product && product.featured_image) || "";
      return (
        '<span class="cart-gwp__thumb cart-gwp__thumb--pending">' +
        renderImg(img, THUMB_IMAGE_WIDTH) +
        "</span>" +
        '<span class="cart-gwp__text cart-gwp__text--lead">Adding ' +
        escapeHtml(name) +
        "&hellip;</span>"
      );
    }
    if (state.pending === "declining") {
      return '<span class="cart-gwp__text cart-gwp__text--lead">Removing gift&hellip;</span>';
    }
    switch (view.ui) {
      case "locked":
        return (
          '<span class="cart-gwp__text">' +
          'Spend <span class="cart-gwp__amount">' +
          escapeHtml(formatMoney(view.nextThresholdCents || 0)) +
          "</span> more to earn a gift" +
          "</span>"
        );
      case "unlocked":
        return view.hasPickableGift
          ? '<span class="cart-gwp__text cart-gwp__text--lead">Choose your gift</span>'
          : '<span class="cart-gwp__text">No gifts are available at the moment</span>';
      case "chosen": {
        const gift = view.existingGift;
        const product = state.products[gift.handle];
        const name =
          (product && product.title) || gift.product_title || "Free gift";
        const img =
          (product && product.featured_image) ||
          (gift.featured_image && gift.featured_image.url) ||
          gift.image ||
          "";
        return (
          '<span class="cart-gwp__thumb">' +
          renderImg(img, THUMB_IMAGE_WIDTH) +
          "</span>" +
          '<span class="cart-gwp__text cart-gwp__text--lead">' +
          escapeHtml(name) +
          "</span>"
        );
      }
      case "dropped":
        return (
          '<span class="cart-gwp__text cart-gwp__text--warn">' +
          'Add <span class="cart-gwp__amount">' +
          escapeHtml(formatMoney(view.nextThresholdCents || 0)) +
          "</span> " +
          "to re-earn your free gift" +
          "</span>"
        );
    }
    return "";
  }

  function renderToggle(view) {
    const label = state.panelOpen
      ? "Done"
      : view.ui === "unlocked"
        ? "Pick"
        : "Change";
    return (
      '<button type="button" class="cart-gwp__toggle"' +
      ' data-gwp-toggle aria-expanded="' +
      state.panelOpen +
      '">' +
      '<span class="cart-gwp__toggle-label">' +
      escapeHtml(label) +
      "</span>" +
      '<span class="cart-gwp__chevron" aria-hidden="true">' +
      ICON_CHEVRON +
      "</span>" +
      "</button>"
    );
  }

  function renderPanel(view) {
    const existingHandle = view.existingGift ? view.existingGift.handle : null;
    const subtotalCents = view.subtotalCents;

    const tiles = ALL_HANDLES.filter((handle) =>
      pickGiftVariant(state.products[handle]),
    ).map((handle) => {
      const product = state.products[handle];
      const chosen = existingHandle === handle;
      const name = product.title;
      const img = product.featured_image || "";
      const unlockAt = unlockThresholdForHandle(handle);
      const isUnlocked = unlockAt != null && subtotalCents >= unlockAt;

      if (!isUnlocked) {
        const remainingCents = (unlockAt || 0) - subtotalCents;
        return (
          '<li class="cart-gwp__tile cart-gwp__tile--locked">' +
          '<div class="cart-gwp__tile-media">' +
          renderImg(img, TILE_IMAGE_WIDTH) +
          '<span class="cart-gwp__tile-lock">' +
          ICON_LOCK +
          "</span>" +
          '<span class="cart-gwp__tile-overlay">+ ' +
          escapeHtml(formatMoney(remainingCents)) +
          "</span>" +
          "</div>" +
          '<span class="cart-gwp__tile-name">' +
          escapeHtml(name) +
          "</span>" +
          "</li>"
        );
      }

      const pending = state.pending === "picking:" + handle;
      return (
        '<li class="cart-gwp__tile' +
        (chosen ? " is-chosen" : "") +
        '">' +
        '<button type="button" class="cart-gwp__tile-btn"' +
        ' data-gwp-handle="' +
        escapeHtml(handle) +
        '"' +
        (chosen ? ' disabled aria-pressed="true"' : ' aria-pressed="false"') +
        (pending ? ' aria-busy="true"' : "") +
        ">" +
        '<span class="cart-gwp__tile-media">' +
        renderImg(img, TILE_IMAGE_WIDTH) +
        (chosen
          ? '<span class="cart-gwp__tile-badge">' + ICON_CHECK + "</span>"
          : "") +
        "</span>" +
        '<span class="cart-gwp__tile-name">' +
        escapeHtml(name) +
        "</span>" +
        "</button>" +
        "</li>"
      );
    }).join("");

    const declining = state.pending === "declining";
    const declineTile =
      '<li class="cart-gwp__tile cart-gwp__tile--decline">' +
      '<button type="button" class="cart-gwp__tile-btn" data-gwp-decline' +
      (declining ? ' aria-busy="true"' : "") +
      ">" +
      '<span class="cart-gwp__tile-media cart-gwp__tile-media--decline">' +
      '<span class="cart-gwp__tile-nope">' +
      ICON_NOPE +
      "</span>" +
      "</span>" +
      '<span class="cart-gwp__tile-name">No thanks</span>' +
      "</button>" +
      "</li>";

    const noGifts = tiles === "" && state.productsReady;
    const canDecline = tiles !== "" || !!view.existingGift;
    return (
      (noGifts
        ? '<p class="cart-gwp__empty">No gifts are available at the moment.</p>'
        : "") +
      (canDecline
        ? '<ul class="cart-gwp__grid">' + tiles + declineTile + "</ul>"
        : "")
    );
  }

  // Couples to theme.js adjustItemPadding() and the quick-cart markup: the footer
  // is absolutely positioned over the item list, which theme.js pads only on
  // open/refresh, so re-pad here when the picker resizes the footer.
  function watchQuickCartFooter(el) {
    const footer = el.closest(".quick-cart__footer");
    if (!footer || typeof ResizeObserver === "undefined") return null;
    const observer = new ResizeObserver(() => {
      const items = footer.parentElement.querySelector(".quick-cart__items");
      if (items) items.style.paddingBottom = footer.clientHeight + "px";
    });
    observer.observe(footer);
    return () => observer.disconnect();
  }

  class CartGwp extends HTMLElement {
    connectedCallback() {
      this._abort = new AbortController();
      this.addEventListener("click", (e) => this._onClick(e), {
        signal: this._abort.signal,
      });
      this._unsub = subscribe(() => render(this));
      this._unwatchFooter = watchQuickCartFooter(this);
      render(this);
      boot();
    }

    disconnectedCallback() {
      if (this._abort) this._abort.abort();
      if (this._unsub) this._unsub();
      if (this._unwatchFooter) this._unwatchFooter();
    }

    _onClick(e) {
      const toggle = e.target.closest("[data-gwp-toggle]");
      if (toggle) {
        e.preventDefault();
        return togglePanel();
      }

      const decline = e.target.closest("[data-gwp-decline]");
      if (decline) {
        e.preventDefault();
        return declineGifts();
      }

      const add = e.target.closest("[data-gwp-handle]");
      if (add && !add.disabled) {
        e.preventDefault();
        pickGift(add.getAttribute("data-gwp-handle"));
      }
    }
  }

  if (!customElements.get("cart-gwp"))
    customElements.define("cart-gwp", CartGwp);

  let prefetchedFor = null;
  function prefetchWhenPickable(s) {
    if (!s.cart || s.cart === prefetchedFor) return;
    prefetchedFor = s.cart;
    const { ui } = computeView(s.cart);
    if (ui !== "unlocked" && ui !== "chosen") return;
    loadMissingProducts();
  }
  subscribe(prefetchWhenPickable);

  document.addEventListener("cart:updated", (e) => {
    enforce(e && e.detail && e.detail.cart);
  });
})();
